import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { StatusBadge, SourceBadge, EnquiryTypeBadge, TagBadge, AlsoInBadge, IntlBadge } from "@/components/Badges";
import { IntlTeamPanel, IntlTabs, IntlAddLeadModal, IntlImportModal } from "@/components/InternationalPanel";
import { toast } from "sonner";
import { Kanban, Table, Plus, MagnifyingGlass, FileX, WhatsappLogo, UploadSimple, Star, Bell, Package, MapPin, Globe } from "@phosphor-icons/react";
import LeadDrawer from "@/components/LeadDrawer";
import { fmtIST } from "@/lib/format";

const STATUSES = ["new", "contacted", "qualified", "converted", "lost"];
const SOURCES = ["IndiaMART", "ExportersIndia", "TradeIndia", "Justdial", "Manual", "WhatsApp", "Website", "Export", "Google Maps"];
const IM_BUYLEAD = "IndiaMART:buylead";   // source-filter value = IndiaMART buy leads (QUERY_TYPE B)

// Compact label filter (Fragvansh): one small button that opens a searchable
// multi-select — scraper keywords are long and numerous, a flat chip wall was
// unusable. Match-any semantics; selections apply instantly, panel stays open
// so several labels can be picked in one go.
function LabelFilter({ allTags, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = React.useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ql = q.trim().toLowerCase();
  const shown = allTags.filter(({ tag }) => !ql || tag.includes(ql));
  const toggle = (tag) =>
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag]);

  return (
    <div className="relative inline-block" ref={boxRef} data-testid="leads-label-filter">
      <button onClick={() => setOpen((v) => !v)}
        className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold border flex items-center gap-1.5 ${
          selected.length ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "border-gray-300 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED] bg-white"}`}
        data-testid="label-filter-btn">
        Labels{selected.length ? ` (${selected.length})` : ""} <span className="text-[8px]">▼</span>
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-80 max-w-[90vw] bg-white border border-gray-300 shadow-lg" data-testid="label-filter-panel">
          <div className="p-2 border-b border-gray-200 flex items-center gap-2">
            <MagnifyingGlass size={12} className="text-gray-400 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search labels…"
              className="flex-1 text-xs outline-none" data-testid="label-filter-search" />
            {selected.length > 0 && (
              <button onClick={() => onChange([])}
                className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-[#E60000] shrink-0"
                data-testid="label-filter-clear">clear ({selected.length})</button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {shown.length === 0 && <div className="p-3 text-xs text-gray-400">No labels match</div>}
            {shown.map(({ tag, count }) => {
              const active = selected.includes(tag);
              return (
                <label key={tag}
                  className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                  data-testid={`label-filter-opt-${tag}`}>
                  <input type="checkbox" checked={active} onChange={() => toggle(tag)}
                    className="rounded border-gray-300 text-[#7C3AED] focus:ring-[#7C3AED] shrink-0" />
                  <span className={`flex-1 ${active ? "font-bold text-[#7C3AED]" : "text-gray-700"}`}>{tag}</span>
                  <span className="text-gray-400 shrink-0">({count})</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Inline follow-up urgency chip for a lead row: red=overdue, amber=due, blue=upcoming.
function FollowupChip({ lead }) {
  if (!lead.followup_due_at) return null;
  const diffMin = (new Date(lead.followup_due_at).getTime() - Date.now()) / 60000;
  const isReorder = lead.followup_type === "reorder";
  let cls, label;
  if (diffMin < -30) { cls = "bg-[#FDECEC] border-[#E60000] text-[#B00000]"; label = "Overdue"; }
  else if (diffMin < 60) { cls = "bg-[#FFF4E5] border-[#E67E00] text-[#B85F00]"; label = "Due"; }
  else { cls = "bg-[#EAF1FF] border-[#002FA7] text-[#002FA7]"; label = "Follow-up"; }
  return (
    <span
      className={`inline-flex items-center gap-0.5 border px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold rounded-sm ${cls}`}
      title={`${isReorder ? "Reorder — " : ""}${lead.followup_note || "Follow-up scheduled"}`}
      data-testid={`lead-followup-chip-${lead.id}`}
    >
      {isReorder ? <Package size={10} weight="bold" /> : <Bell size={10} weight="bold" />}
      {isReorder ? "Reorder" : label}
    </span>
  );
}

// `international` = the International section (sidebar): same list, scoped to
// international leads, with the team panel, tabs and manual entry on top.
export default function Leads({ international = false }) {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { user } = useAuth();
  const { isFragvansh } = useCompany();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [execs, setExecs] = useState([]);
  const [view, setView] = useState(params.get("view") || "table");
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "");
  const [sourceFilter, setSourceFilter] = useState(params.get("source") || "");
  const [assignedFilter, setAssignedFilter] = useState(params.get("assigned") || "");
  const [outcomeFilter, setOutcomeFilter] = useState(params.get("outcome") || "");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [starredFilter, setStarredFilter] = useState(params.get("starred") === "true");
  const [reassignedFilter, setReassignedFilter] = useState(params.get("reassigned") === "1");
  // "" | "any" (ordered before) | months since last order: "6" / "12" / "24"
  const [lastOrderFilter, setLastOrderFilter] = useState(params.get("last_order") || "");
  // Tag filter (Fragvansh labels): multi-select, match-any
  const [tagFilter, setTagFilter] = useState((params.get("tags") || "").split(",").filter(Boolean));
  const [allTags, setAllTags] = useState([]);
  const [gmapsImporting, setGmapsImporting] = useState(false);
  const [openId, setOpenId] = useState(params.get("lead") || null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10) || 1);
  const [pageSize, setPageSize] = useState(parseInt(params.get("size") || "25", 10) || 25);
  const [total, setTotal] = useState(0);
  const [intlTab, setIntlTab] = useState(params.get("tab") || "all");
  const [countryFilter, setCountryFilter] = useState(params.get("country") || "");
  const [intlInfo, setIntlInfo] = useState(null);
  const [intlAdding, setIntlAdding] = useState(false);
  const [intlImporting, setIntlImporting] = useState(false);
  const [intlRefresh, setIntlRefresh] = useState(0);

  // Monotonic request id: a slow earlier response must never overwrite the
  // results of a newer search (classic stale-response race while typing).
  const loadSeq = React.useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    try {
      const { data } = await api.get("/leads", {
        params: {
          q: q || undefined,
          status: statusFilter || undefined,
          // "IndiaMART:buylead" is a sub-option of the source filter
          source: sourceFilter === IM_BUYLEAD ? "IndiaMART" : (sourceFilter || undefined),
          lead_type: sourceFilter === IM_BUYLEAD ? "im_buylead" : undefined,
          assigned_to: assignedFilter || undefined,
          last_call_outcome: outcomeFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          starred: starredFilter ? true : undefined,
          reassigned: reassignedFilter ? true : undefined,
          past_customers: lastOrderFilter === "any" ? true : undefined,
          last_order_before: /^\d+$/.test(lastOrderFilter) ? monthsAgoISO(Number(lastOrderFilter)) : undefined,
          tags: tagFilter.length ? tagFilter.join(",") : undefined,
          intl: international ? intlTab : undefined,
          country: international && countryFilter ? countryFilter : undefined,
          paginate: true,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      });
      if (seq !== loadSeq.current) return; // superseded by a newer request
      // /api/leads returns {items,total,...} when paginate=true is sent.
      setLeads(data?.items || []);
      setTotal(typeof data?.total === "number" ? data.total : (data?.items || []).length);
    } catch (e) { if (seq === loadSeq.current) toast.error(errMsg(e)); }
  };

  const toggleStar = async (leadId, currentStarred) => {
    try {
      setLeads((prev) =>
        prev.map((ld) => (ld.id === leadId ? { ...ld, starred: !currentStarred } : ld))
      );
      await api.patch(`/leads/${leadId}`, { starred: !currentStarred });
      toast.success(currentStarred ? "Lead unstarred" : "Lead starred");
    } catch (e) {
      toast.error(errMsg(e));
      setLeads((prev) =>
        prev.map((ld) => (ld.id === leadId ? { ...ld, starred: currentStarred } : ld))
      );
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        // Include both admins and executives so admin can self-assign or reassign-to-self.
        // Round-robin (auto-assign) backend-side still excludes admins.
        setExecs(data.filter((u) => u.role === "executive" || u.role === "admin"));
      } catch { /* empty */ }
    })();
  }, []);

  // Fragvansh: distinct tags for the filter chips
  useEffect(() => {
    if (!isFragvansh) return;
    (async () => {
      try {
        const { data } = await api.get("/leads/tags");
        setAllTags(data || []);
      } catch { /* empty */ }
    })();
  }, [isFragvansh]);

  // Reset to first page whenever a filter changes (so we never end up on an
  // empty page after narrowing the result set).
  useEffect(() => { setPage(1); }, [statusFilter, sourceFilter, assignedFilter, outcomeFilter, dateFrom, dateTo, q, pageSize, starredFilter, reassignedFilter, lastOrderFilter, tagFilter, intlTab, countryFilter]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sourceFilter, assignedFilter, outcomeFilter, dateFrom, dateTo, page, pageSize, starredFilter, reassignedFilter, lastOrderFilter, tagFilter, intlTab, countryFilter]);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    const p = {};
    if (view !== "table") p.view = view;
    if (q) p.q = q;
    if (statusFilter) p.status = statusFilter;
    if (sourceFilter) p.source = sourceFilter;
    if (assignedFilter) p.assigned = assignedFilter;
    if (outcomeFilter) p.outcome = outcomeFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (starredFilter) p.starred = "true";
    if (reassignedFilter) p.reassigned = "1";
    if (lastOrderFilter) p.last_order = lastOrderFilter;
    if (tagFilter.length) p.tags = tagFilter.join(",");
    if (international && intlTab !== "all") p.tab = intlTab;
    if (international && countryFilter) p.country = countryFilter;
    if (openId) p.lead = openId;
    if (page > 1) p.page = String(page);
    if (pageSize !== 25) p.size = String(pageSize);
    setParams(p, { replace: true });
  }, [view, q, statusFilter, sourceFilter, assignedFilter, outcomeFilter, dateFrom, dateTo, starredFilter, reassignedFilter, lastOrderFilter, tagFilter, openId, page, pageSize, setParams, international, intlTab, countryFilter]);

  const execMap = useMemo(() => Object.fromEntries(execs.map((e) => [e.id, e])), [execs]);
  const isAdmin = user.role === "admin";
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-4 md:p-8 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{international ? "International" : "Pipeline"}</div>
          <h1 className="font-chivo font-black text-2xl md:text-4xl">{international ? "International Leads" : "All Leads"} <span className="text-gray-400">[{total}]</span></h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            className={`border px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${view === "table" ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 hover:bg-gray-100"}`}
            onClick={() => setView("table")} data-testid="view-table-btn"
          ><Table size={12} weight="bold" /> Table</button>
          <button
            className={`border px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${view === "kanban" ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 hover:bg-gray-100"}`}
            onClick={() => setView("kanban")} data-testid="view-kanban-btn"
          ><Kanban size={12} weight="bold" /> Kanban</button>
          {international ? (
            <>
              {isAdmin && intlInfo?.feed && (
                <button
                  className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                  onClick={() => setIntlImporting(true)} data-testid="intl-import-btn"
                ><MapPin size={12} weight="bold" /> Import scraper CSV</button>
              )}
              <button
                className="bg-[#002FA7] hover:bg-[#002288] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                onClick={() => setIntlAdding(true)} data-testid="intl-add-btn"
              ><Globe size={12} weight="bold" /> Add international lead</button>
            </>
          ) : (<>
          <button
            className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
            onClick={() => setUploading(true)} data-testid="upload-leads-btn"
          ><UploadSimple size={12} weight="bold" /> Upload Excel</button>
          {isFragvansh && (
            <button
              className="border border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF] px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
              onClick={() => setGmapsImporting(true)} data-testid="gmaps-import-btn"
            ><MapPin size={12} weight="bold" /> Import GMaps CSV</button>
          )}
          <button
            className="bg-[#002FA7] hover:bg-[#002288] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
            onClick={() => setCreating(true)} data-testid="new-lead-btn"
          ><Plus size={12} weight="bold" /> New Lead</button>
          </>)}
        </div>
      </div>

      {international && (
        <>
          <IntlTeamPanel isAdmin={isAdmin} refreshKey={intlRefresh} onInfo={setIntlInfo} />
          <IntlTabs tab={intlTab} onTab={setIntlTab} isAdmin={isAdmin} pending={intlInfo?.pending} />
        </>
      )}

      {/* Filters - on a phone only the search box shows until "Filters" is tapped */}
      <div className={`border border-gray-200 bg-white p-3 md:p-4 grid grid-cols-1 md:grid-cols-5 gap-2 md:gap-3 ${showMobileFilters ? "" : "leads-filters-collapsed"}`}>
        <div className="relative md:col-span-2">
          <MagnifyingGlass className="absolute left-2 top-2.5 text-gray-400" size={14} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, requirement, city"
            className="w-full border border-gray-300 pl-7 pr-3 py-2 text-sm outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]"
            data-testid="leads-search-input"
          />
        </div>
        <button type="button" data-filters-toggle="1" onClick={() => setShowMobileFilters((v) => !v)}
          className="md:hidden border border-gray-300 px-3 py-2 text-xs uppercase tracking-widest font-bold text-gray-700 text-left"
          data-testid="leads-filters-toggle">
          {showMobileFilters ? "Hide filters ▴" : `Filters${[statusFilter, sourceFilter, assignedFilter, outcomeFilter, starredFilter].filter(Boolean).length ? ` (${[statusFilter, sourceFilter, assignedFilter, outcomeFilter, starredFilter].filter(Boolean).length} on)` : ""} ▾`}
        </button>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm" data-testid="leads-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm" data-testid="leads-source-filter">
          <option value="">All sources</option>
          {SOURCES.map((s) => (
            <React.Fragment key={s}>
              <option value={s}>{s}</option>
              {s === "IndiaMART" && <option value={IM_BUYLEAD}>IndiaMART — Buy leads only</option>}
            </React.Fragment>
          ))}
        </select>
        {reassignedFilter && (
          <button type="button" onClick={() => setReassignedFilter(false)} data-testid="leads-reassigned-chip"
            className="border border-[#002FA7] bg-[#F0F4FF] text-[#002FA7] px-2 py-2 text-xs font-bold whitespace-nowrap" title="Remove this filter">
            Reassigned leads only ✕
          </button>
        )}
        {isAdmin && (
          <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm" data-testid="leads-assignee-filter">
            <option value="">All executives</option>
            {execs.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
        <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm md:col-span-1 col-span-1" data-testid="leads-outcome-filter">
          <option value="">All call outcomes</option>
          <option value="connected">Connected</option>
          <option value="no_response">No Response (PNR)</option>
          <option value="not_reachable">Not Reachable</option>
          <option value="rejected">Rejected</option>
          <option value="busy">Busy / Engaged</option>
          <option value="invalid">Invalid</option>
        </select>
        {international && (
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm" data-testid="intl-country-filter">
            <option value="">All countries</option>
            {(intlInfo?.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {!international && (
          <select value={lastOrderFilter} onChange={(e) => setLastOrderFilter(e.target.value)} data-testid="leads-last-order-filter"
            className={`border px-2 py-2 text-sm ${lastOrderFilter ? "border-[#002FA7] bg-[#F0F4FF] text-[#002FA7] font-semibold" : "border-gray-300"}`}
            title="Past customers by their last order (old order sheets + OMS)">
            <option value="">Last order: any lead</option>
            <option value="any">Past customers (ordered before)</option>
            <option value="6">No order in 6+ months</option>
            <option value="12">No order in 1+ year</option>
            <option value="24">No order in 2+ years</option>
          </select>
        )}
        <select value={starredFilter ? "true" : ""} onChange={(e) => setStarredFilter(e.target.value === "true")} className="border border-gray-300 px-2 py-2 text-sm" data-testid="leads-starred-filter">
          <option value="">All leads</option>
          <option value="true">Starred leads only</option>
        </select>
        {/* Date range — single date = same value in both fields. Inclusive (IST). */}
        <div className="flex items-center gap-1 col-span-2 md:col-span-2" data-testid="leads-date-filter">
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            max={dateTo || undefined}
            className="flex-1 border border-gray-300 px-2 py-2 text-sm" title="From date" data-testid="leads-date-from"
          />
          <span className="text-gray-400 text-xs">→</span>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            className="flex-1 border border-gray-300 px-2 py-2 text-sm" title="To date" data-testid="leads-date-to"
          />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="border border-gray-300 hover:border-gray-900 px-2 py-2 text-[10px] uppercase tracking-widest font-bold text-gray-500 hover:text-gray-900" title="Clear date filter" data-testid="leads-date-clear">
              ×
            </button>
          )}
        </div>
      </div>

      {/* Label filter (Fragvansh) — compact searchable multi-select dropdown */}
      {isFragvansh && allTags.length > 0 && (
        <LabelFilter allTags={allTags} selected={tagFilter} onChange={setTagFilter} />
      )}

      {view === "table" ? (
        <>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPage={setPage}
            onPageSize={setPageSize}
          />
          {/* MOBILE: card list */}
          <div className="md:hidden space-y-2" data-testid="leads-mobile-list">
            {leads.length === 0 ? (
              <div className="border border-gray-200 bg-white p-8 text-center">
                <FileX size={48} weight="light" className="mx-auto text-gray-300" />
                <div className="mt-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold">No leads yet</div>
              </div>
            ) : leads.map((l) => {
              const unread = !l.opened_at;
              const ageMin = Math.floor((Date.now() - new Date(l.created_at).getTime()) / 60000);
              const overdue = unread && ageMin > 15;
              return (
                <div
                  key={l.id}
                  onClick={() => setOpenId(l.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(l.id); } }}
                  role="button"
                  tabIndex={0}
                  className={`block w-full text-left border border-gray-200 bg-white p-3 hover:border-gray-900 transition-colors cursor-pointer ${overdue ? "border-l-2 border-l-[#E60000]" : ""}`}
                  data-testid={`lead-card-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`truncate ${unread ? "font-bold" : "font-semibold"}`}>{l.customer_name}</span>
                        <LastOrderTag l={l} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(l.id, l.starred);
                          }}
                          className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ${
                            l.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
                          }`}
                          title={l.starred ? "Unstar lead" : "Star lead"}
                          data-testid={`lead-star-btn-mobile-${l.id}`}
                        >
                          <Star size={14} weight={l.starred ? "fill" : "regular"} />
                        </button>
                        <FollowupChip lead={l} />
                      </div>
                      {l.phone && (
                        <div className="text-xs text-gray-500 font-mono mt-0.5 flex items-center gap-1.5">
                          <span>{l.phone}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); nav(`/chat?lead=${l.id}`); }}
                            className="text-[#25D366] hover:bg-[#25D366] hover:text-white p-0.5 rounded transition-colors"
                            title="Open WhatsApp chat for this lead"
                            data-testid={`leads-card-wa-${l.id}`}
                          >
                            <WhatsappLogo size={11} weight="fill" />
                          </button>
                          {l.phones?.length > 0 && (
                            <span className="ml-1 text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">+{l.phones.length}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={l.status} />
                  </div>
                  {l.requirement && (
                    <div className="text-xs text-gray-700 mt-2 line-clamp-2">{l.requirement}</div>
                  )}
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <SourceBadge source={l.source} />
                    <IntlBadge lead={l} />
                    <EnquiryTypeBadge lead={l} />
                    <AlsoInBadge lead={l} />
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold ml-auto">
                      {execMap[l.assigned_to]?.name || "Unassigned"}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400 font-mono mt-1">{fmtIST(l.created_at)}</div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP: table */}
          <div className="hidden md:block border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Requirement</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assigned</th>
                <th className="text-left px-4 py-3">Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const unread = !l.opened_at;
                const ageMin = Math.floor((Date.now() - new Date(l.created_at).getTime()) / 60000);
                const overdue = unread && ageMin > 15;
                return (
                  <tr key={l.id} onClick={() => setOpenId(l.id)}
                    className={`border-t border-gray-200 cursor-pointer hover:bg-gray-50 ${overdue ? "border-l-2 border-l-[#E60000]" : ""}`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`${unread ? "font-bold" : ""}`}>{l.customer_name}</span>
                        <LastOrderTag l={l} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(l.id, l.starred);
                          }}
                          className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ${
                            l.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
                          }`}
                          title={l.starred ? "Unstar lead" : "Star lead"}
                          data-testid={`lead-star-btn-${l.id}`}
                        >
                          <Star size={16} weight={l.starred ? "fill" : "regular"} />
                        </button>
                        <FollowupChip lead={l} />
                        <AlsoInBadge lead={l} />
                      </div>
                      {l.phone && (
                        <div className="text-xs text-gray-500 font-mono flex items-center gap-1.5">
                          <span>{l.phone}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); nav(`/chat?lead=${l.id}`); }}
                            className="text-[#25D366] hover:bg-[#25D366] hover:text-white p-0.5 rounded transition-colors"
                            title="Open WhatsApp chat for this lead"
                            data-testid={`leads-row-wa-${l.id}`}
                          >
                            <WhatsappLogo size={11} weight="fill" />
                          </button>
                          {l.phones?.length > 0 && (
                            <span className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">
                              +{l.phones.length}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[260px] truncate">{l.requirement || "—"}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{[l.area, l.city, l.state, l.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1"><SourceBadge source={l.source} /><IntlBadge lead={l} /></div>
                    </td>
                    <td className="px-4 py-3">
                      <EnquiryTypeBadge lead={l} />
                      {l.source_data?.QUERY_TYPE === "P" && l.source_data?.RECEIVER_MOBILE && (
                        <div className="text-[10px] font-mono text-gray-500 mt-1" title="PNS Receiver Number">
                          Rcv: {l.source_data.RECEIVER_MOBILE}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-xs">{execMap[l.assigned_to]?.name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{fmtIST(l.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/leads/${l.id}`} onClick={(e) => e.stopPropagation()} className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7]" data-testid={`open-lead-${l.id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {leads.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-16 text-center">
                  <FileX size={56} weight="light" className="mx-auto text-gray-300" />
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold">No leads yet</div>
                  <div className="text-xs text-gray-400 mt-1">Create a lead or trigger the IndiaMART webhook to populate the pipeline.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPage={setPage}
          onPageSize={setPageSize}
        />
        </>
      ) : (
        <>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPage={setPage}
            onPageSize={setPageSize}
          />
          <Kanban_ leads={leads} onOpen={setOpenId} execMap={execMap} onToggleStar={toggleStar} />
        </>
      )}

      {openId && <LeadDrawer leadId={openId} onClose={() => { setOpenId(null); load(); }} />}
      {creating && <NewLeadModal execs={execs} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); load(); if (id) setOpenId(id); }} isAdmin={isAdmin} />}
      {uploading && <UploadLeadsModal execs={execs} onClose={() => setUploading(false)} onUploaded={() => { setUploading(false); load(); }} isAdmin={isAdmin} />}
      {gmapsImporting && <GmapsImportModal onClose={() => setGmapsImporting(false)} onImported={() => { setGmapsImporting(false); load(); }} />}
      {intlAdding && <IntlAddLeadModal members={intlInfo?.members || []} isAdmin={isAdmin} onClose={() => setIntlAdding(false)} onCreated={(id) => { setIntlAdding(false); load(); setIntlRefresh((n) => n + 1); if (id) setOpenId(id); }} />}
      {intlImporting && intlInfo?.feed && <IntlImportModal ingestKey={intlInfo.feed.ingest_key} onClose={() => setIntlImporting(false)} onImported={() => { setIntlImporting(false); load(); setIntlRefresh((n) => n + 1); }} />}
    </div>
  );
}

// ---- Google Maps CSV import (Fragvansh) ----
// Parses the scraper's CSV client-side and batches it to /ingest/gmaps — the
// same endpoint the Colab notebook streams to, so dedup/tagging is identical.
function parseCsv(text) {
  const rows = [];
  let cur = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { cur.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field); field = "";
      if (cur.some((f) => f !== "")) rows.push(cur);
      cur = [];
    } else field += c;
  }
  if (field !== "" || cur.length) { cur.push(field); if (cur.some((f) => f !== "")) rows.push(cur); }
  return rows;
}

function GmapsImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [ingestKey, setIngestKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // The ingest key is shown in Settings → Integration URLs; fetch it here so the
  // admin doesn't have to copy-paste.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/webhooks-info");
        if (data?.gmaps?.ingest_key) setIngestKey(data.gmaps.ingest_key);
      } catch { /* non-admin or citspray view — manual key entry below */ }
    })();
  }, []);

  const run = async () => {
    if (!file) return toast.error("Choose the scraper CSV first");
    if (!ingestKey.trim()) return toast.error("Ingest key required (Settings → Integration URLs → Google Maps)");
    setBusy(true);
    setResult(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) throw new Error("CSV appears empty");
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const records = rows.slice(1).map((r) => {
        const rec = {};
        header.forEach((h, i) => { if (r[i] !== undefined && r[i] !== "") rec[h] = r[i]; });
        return rec;
      }).filter((r) => r.name || r.phone);
      let totals = { created: 0, duplicates: 0, skipped_no_phone: 0 };
      for (let i = 0; i < records.length; i += 100) {
        const { data } = await api.post("/ingest/gmaps", { records: records.slice(i, i + 100) },
          { headers: { "X-Ingest-Key": ingestKey.trim() } });
        totals = {
          created: totals.created + (data.created || 0),
          duplicates: totals.duplicates + (data.duplicates || 0),
          skipped_no_phone: totals.skipped_no_phone + (data.skipped_no_phone || 0),
        };
      }
      setResult(totals);
      toast.success(`Imported: ${totals.created} new · ${totals.duplicates} duplicates · ${totals.skipped_no_phone} without phone`);
      onImported();
    } catch (e) {
      toast.error(errMsg(e, "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="gmaps-import-modal">
      <div className="bg-white border border-gray-200 w-full max-w-md p-5 space-y-4">
        <div className="font-chivo font-black text-lg">Import Google Maps CSV</div>
        <p className="text-xs text-gray-500">
          Upload a CSV from the Google Maps scraper. Each row becomes a Fragvansh lead
          (source: Google Maps), tagged with its search keyword, round-robin assigned.
          Rows without a phone number are skipped; existing numbers are left untouched.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="gmaps-csv-input" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Ingest key</div>
          <input value={ingestKey} onChange={(e) => setIngestKey(e.target.value)} placeholder="Auto-filled for admins"
            className="w-full border border-gray-300 px-2 py-2 text-sm font-mono" data-testid="gmaps-key-input" />
        </div>
        {result && (
          <div className="text-xs bg-gray-50 border border-gray-200 p-2 font-mono">
            created: {result.created} · duplicates: {result.duplicates} · no phone: {result.skipped_no_phone}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border border-gray-300 px-3 py-2 text-[10px] uppercase tracking-widest font-bold" disabled={busy}>Close</button>
          <button onClick={run} disabled={busy || !file}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
            data-testid="gmaps-import-run">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kanban_({ leads, onOpen, execMap, onToggleStar }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border border-gray-200 bg-white">
      {STATUSES.map((s, i) => {
        const items = leads.filter((l) => l.status === s);
        return (
          <div key={s} className={`border-gray-200 ${i < 4 ? "md:border-r" : ""} min-h-[400px]`} data-testid={`kanban-col-${s}`}>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{s}</div>
              <div className="font-mono text-xs">{items.length}</div>
            </div>
            <div className="p-2 space-y-2">
              {items.map((l) => (
                <div key={l.id} onClick={() => onOpen(l.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(l.id); } }}
                  role="button"
                  tabIndex={0}
                  className="w-full text-left border border-gray-200 bg-white p-3 hover:border-gray-900 transition-colors cursor-pointer"
                  data-testid={`kanban-card-${l.id}`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-semibold text-sm truncate">{l.customer_name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleStar(l.id, l.starred);
                        }}
                        className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0 ${
                          l.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
                        }`}
                        title={l.starred ? "Unstar lead" : "Star lead"}
                        data-testid={`lead-star-btn-kanban-${l.id}`}
                      >
                        <Star size={14} weight={l.starred ? "fill" : "regular"} />
                      </button>
                    </div>
                    <SourceBadge source={l.source} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{l.requirement || "—"}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <EnquiryTypeBadge lead={l} />
                    <AlsoInBadge lead={l} />
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                      {execMap[l.assigned_to]?.name || "Unassigned"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated, execs, isAdmin }) {
  const [f, setF] = useState({ customer_name: "", phone: "", requirement: "", city: "", state: "", area: "", source: "Manual", gst_no: "", enquiry_type: "", assigned_to: "" });
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(null); // { existing_lead_id, owned_by_name, message }
  const [requesting, setRequesting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setConflict(null);
    try {
      const payload = { ...f };
      if (!payload.assigned_to) delete payload.assigned_to;
      const { data } = await api.post("/leads", payload);
      if (data?.duplicate || data?.existed) {
        toast.success(`Existing lead opened: ${data.customer_name}`);
        onCreated(data.id);  // signal which lead to open
        return;
      }
      toast.success("Lead created");
      onCreated(data?.id);
    } catch (err) {
      // Structured 409 from backend → show inline reassignment CTA instead of red toast
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_phone") {
        setConflict(detail);
      } else {
        toast.error(errMsg(err));
      }
    } finally { setLoading(false); }
  };

  const requestReassignment = async () => {
    if (!conflict?.existing_lead_id) return;
    setRequesting(true);
    try {
      await api.post("/inbox/transfer-request", {
        lead_id: conflict.existing_lead_id,
        reason: `Duplicate add attempt for phone ${f.phone || ""}. Customer name: ${f.customer_name || ""}.`,
      });
      toast.success("Reassignment request sent to admin");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRequesting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-lead-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-xl bg-white border border-gray-900 p-6"
      >
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Create</div>
        <h2 className="font-chivo font-black text-2xl mt-1 mb-4">New Lead</h2>
        {conflict && (
          <div className="border-l-4 border-[#E60000] bg-[#FFE9E9] px-4 py-3 mb-4" data-testid="duplicate-conflict-panel">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#E60000]">Duplicate phone</div>
            <div className="text-sm mt-1">{conflict.message}</div>
            <div className="text-xs text-gray-700 mt-1">
              Currently with: <b>{conflict.owned_by_name || "another executive"}</b>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={requestReassignment}
                disabled={requesting}
                className="bg-[#E60000] hover:bg-[#cc0000] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
                data-testid="request-reassignment-btn"
              >
                {requesting ? "Sending…" : "Request Reassignment"}
              </button>
              <button
                type="button"
                onClick={() => setConflict(null)}
                className="border border-gray-300 px-3 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100"
              >
                Edit phone
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer name *"><input required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-name-input" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" placeholder="8790934618 or +255123456789" data-testid="new-lead-phone-input" /></Field>
          <Field label="Requirement" full><input value={f.requirement} onChange={(e) => setF({ ...f, requirement: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-requirement-input" /></Field>
          <Field label="Area"><input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-area-input" /></Field>
          <Field label="City"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-city-input" /></Field>
          <Field label="State"><input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-state-input" /></Field>
          <Field label="GST No."><input value={f.gst_no} onChange={(e) => setF({ ...f, gst_no: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-lead-gst-input" /></Field>
          <Field label="Enquiry Type"><input value={f.enquiry_type} onChange={(e) => setF({ ...f, enquiry_type: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. direct, buyleads" data-testid="new-lead-enquiry-type-input" /></Field>
          <Field label="Source">
            <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="new-lead-source-select">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          {isAdmin && (
            <Field label="Assign to" full>
              <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="new-lead-assign-select">
                <option value="">Auto (round-robin)</option>
                {execs.map((x) => <option key={x.id} value={x.id}>{x.role === "admin" ? `${x.name} (admin)` : x.name}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100" data-testid="new-lead-cancel-btn">Cancel</button>
          <button disabled={loading} className="bg-[#002FA7] hover:bg-[#002288] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="new-lead-submit-btn">
            {loading ? "Creating…" : "Create Lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

function UploadLeadsModal({ onClose, onUploaded, execs, isAdmin }) {
  const [file, setFile] = useState(null);
  const [assignedTo, setAssignedTo] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const f = e.dataTransfer.files[0];
      if (f.name.endsWith(".xlsx") || f.name.endsWith(".xls") || f.name.endsWith(".csv")) {
        setFile(f);
      } else {
        toast.error("Please upload an Excel (.xlsx, .xls) or CSV (.csv) file");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/leads/upload-template", {
        responseType: "blob"
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "leads_upload_template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Template download started");
    } catch (e) {
      toast.error("Failed to download template: " + errMsg(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    if (assignedTo) {
      formData.append("assigned_to", assignedTo);
    }
    if (source) {
      formData.append("source", source);
    }

    try {
      const { data } = await api.post("/leads/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      setResult(data);
      if (data.summary.imported > 0) {
        toast.success(`Successfully imported ${data.summary.imported} leads!`);
        onUploaded();
      } else if (data.summary.duplicates > 0) {
        toast.success(`Processed ${data.summary.duplicates} repeat enquiries.`);
        onUploaded();
      } else {
        toast.error("No new leads were imported.");
      }
    } catch (err) {
      toast.error("Failed to upload leads: " + errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="upload-leads-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border border-gray-900 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Import</div>
            <h2 className="font-chivo font-black text-2xl mt-1 mb-2">Upload Leads via Excel</h2>
          </div>
          <button 
            onClick={downloadTemplate}
            type="button"
            className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline flex items-center gap-1 border border-[#002FA7]/30 px-2.5 py-1.5 rounded bg-[#002FA7]/5 hover:bg-[#002FA7]/10"
          >
            Download Template
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {isAdmin && (
                <Field label="Assign Imported Leads To">
                  <select 
                    value={assignedTo} 
                    onChange={(e) => setAssignedTo(e.target.value)} 
                    className="w-full border border-gray-300 px-2 py-2 text-sm"
                    data-testid="upload-assign-select"
                  >
                    <option value="">Auto (round-robin)</option>
                    {execs.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.role === "admin" ? `${x.name} (admin)` : x.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Enquiry Source (Default/Override)" full={!isAdmin}>
                <select 
                  value={source} 
                  onChange={(e) => setSource(e.target.value)} 
                  className="w-full border border-gray-300 px-2 py-2 text-sm"
                  data-testid="upload-source-select"
                >
                  <option value="">Use source from file (fallback to "Excel Upload")</option>
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div 
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                dragActive ? "border-[#002FA7] bg-[#002FA7]/5 scale-[0.99]" : "border-gray-300 hover:border-gray-900 bg-gray-50"
              }`}
              onClick={() => document.getElementById("excel-file-input").click()}
            >
              <input 
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
              />
              <UploadSimple size={36} className="mx-auto text-gray-400 mb-2" />
              {file ? (
                <div>
                  <p className="text-sm font-bold text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · Click or drag to replace</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-700 font-medium">Drag & drop your Excel or CSV file here</p>
                  <p className="text-xs text-gray-500 mt-1">or click to browse from your computer</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button 
                type="button" 
                onClick={onClose} 
                className="border border-gray-300 px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100"
              >
                Cancel
              </button>
              <button 
                disabled={loading || !file} 
                type="submit"
                className="bg-[#002FA7] hover:bg-[#002288] text-white px-5 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50 flex items-center gap-1.5"
              >
                {loading ? "Processing..." : "Import Leads"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4 mt-6">
            <div className="border border-gray-200 p-4 bg-gray-50 grid grid-cols-4 gap-2 text-center">
              <div className="border-r border-gray-200">
                <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Total Rows</div>
                <div className="text-2xl font-black mt-1 text-gray-900">{result.summary.total}</div>
              </div>
              <div className="border-r border-gray-200">
                <div className="text-xs text-[#002FA7] uppercase tracking-wider font-semibold">Imported</div>
                <div className="text-2xl font-black mt-1 text-[#002FA7]">{result.summary.imported}</div>
              </div>
              <div className="border-r border-gray-200">
                <div className="text-xs text-amber-600 uppercase tracking-wider font-semibold">Duplicates</div>
                <div className="text-2xl font-black mt-1 text-amber-600">{result.summary.duplicates}</div>
              </div>
              <div>
                <div className="text-xs text-rose-600 uppercase tracking-wider font-semibold">Errors</div>
                <div className="text-2xl font-black mt-1 text-rose-600">{result.summary.errors}</div>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-rose-600">Error Logs</h3>
                <div className="max-h-48 overflow-y-auto border border-rose-200 bg-rose-50/50 p-3 rounded font-mono text-xs text-rose-700 space-y-1.5">
                  {result.errors.map((err, i) => (
                    <div key={i}>• {err}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button 
                type="button" 
                onClick={onClose} 
                className="bg-gray-900 hover:bg-black text-white px-5 py-2 text-[10px] uppercase tracking-widest font-bold"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`${full ? "md:col-span-2" : ""} block`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}

function PaginationBar({ page, pageSize, total, totalPages, onPage, onPageSize }) {
  const [jump, setJump] = React.useState("");
  if (total === 0) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const goPrev = () => onPage(Math.max(1, page - 1));
  const goNext = () => onPage(Math.min(totalPages, page + 1));
  const goToPage = () => {
    const n = parseInt(jump, 10);
    if (!Number.isFinite(n)) return;
    onPage(Math.min(totalPages, Math.max(1, n)));
    setJump("");
  };
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 bg-white px-3 py-2"
      data-testid="leads-pagination-bar"
    >
      <div className="text-xs text-gray-500" data-testid="leads-pagination-summary">
        Showing <span className="font-mono font-bold text-gray-900">{start}–{end}</span> of <span className="font-mono font-bold text-gray-900">{total}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Per page
          <select
            value={pageSize}
            onChange={(e) => onPageSize(parseInt(e.target.value, 10))}
            className="border border-gray-300 px-1.5 py-1 text-xs"
            data-testid="leads-pagination-size"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          onClick={goPrev}
          disabled={page <= 1}
          className="border border-gray-300 px-3 py-1 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="leads-pagination-prev"
        >
          Prev
        </button>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-mono" data-testid="leads-pagination-page">
          {page} / {totalPages}
        </span>
        <button
          onClick={goNext}
          disabled={page >= totalPages}
          className="border border-gray-300 px-3 py-1 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="leads-pagination-next"
        >
          Next
        </button>
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Go to
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") goToPage(); }}
            placeholder={String(page)}
            className="border border-gray-300 px-1.5 py-1 text-xs w-16 font-mono"
            data-testid="leads-pagination-jump-input"
          />
          <button
            onClick={goToPage}
            className="border border-gray-900 px-2 py-1 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-900 hover:text-white"
            data-testid="leads-pagination-jump-btn"
          >
            Go
          </button>
        </span>
      </div>
    </div>
  );
}

// YYYY-MM-DD of N months ago (IST is fine at day precision)
function monthsAgoISO(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

// small "last order" tag next to the name for past customers
function LastOrderTag({ l }) {
  if (!l || !l.last_order_at) return null;
  const days = Math.floor((Date.now() - new Date(l.last_order_at + "T00:00:00").getTime()) / 86400000);
  const label = days < 45 ? `${days}d` : days < 365 ? `${Math.round(days / 30)} mo` : `${(days / 365).toFixed(days < 730 ? 1 : 0)} yr`;
  const cls = days > 365 ? "border-[#B00000]/30 text-[#B00000] bg-[#FDECEC]" : days > 180 ? "border-[#8A6400]/30 text-[#8A6400] bg-[#FFF6DB]" : "border-[#0A7A35]/30 text-[#0A7A35] bg-[#E7F6EC]";
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider border px-1 py-0.5 whitespace-nowrap ${cls}`}
      title={`Last order ${l.last_order_at}${l.past_orders_count ? ` · ${l.past_orders_count} old-sheet orders` : ""}`} data-testid="last-order-tag">
      Last order {label} ago
    </span>
  );
}
